import {produce} from 'immer'

import {restBaseApi} from '../../../../services/restBase'
import type {PipelineId, ProjectId} from '../../../../types'
import type {KotlinDslRequestBody} from '../EditPipelinePage/EditPipelinePage.types'
import {
  getPipelinesSettingsWithoutDeprecatedFields,
  getPreformattingIntegrations,
  getPreformattingSettings,
  parsePipeline,
  preformatPipelineVcsData,
  stringifyPipeline,
} from '../EditPipelinePage/EditPipelinePage.utils'
import {getAccountRootProjectId} from '../PipelinesPages.consts'
import type {PipelineSettings} from '../types'

import type {
  NewPipelineDto,
  PipelineDto,
  Pipeline,
  UpdatePipelineArg,
  NewPipeline,
  GetRepositoryBranchesArg,
  RepositoryTestConnectionArg,
  PipelineDurations,
  RunDurations,
  PipelineFeaturesJsonFromXMLs,
  PipelineRunnersJsonFromXMLs,
  GetParametersResolveArg,
  CheckVersionedSettingsArg,
  GetEffectiveVersionedSettingsConfigArg,
  EffectiveVersionedSettingsConfig,
  PipelineParametersResolve,
  CreateNewPipelineArg,
  PipelineProviderJdk,
  GetJobDescriptionsArg,
  GetJobDescriptions,
  BuildStepDescriptions,
  GetBuildStepDescription,
  GetBuildStepDescriptionArg,
  AgentCompatibilitiesArg,
  AgentCompatibilities,
  JobMapAgentCompatibilities,
  GetCompatibilityAgentsQueuedJobArg,
  VcsProvider,
  GetRepositoriesArg,
  GetRepositoriesResponse,
  GetPipelineRunVcsRootsArg,
  VcsRootDto,
  DebugArg,
  DebugResponse,
  GetProjectSshKeysArg,
  GetPipelineSshKeysArg,
  ProjectSshKeys,
  NotificationsArg,
  PersonalNotificationsDto,
  ProjectWithAncestorsDto,
  GetParentParametersArg,
  GroupedAvailableVcsProviders,
  ParameterWithType,
  PipelineYamlSchema,
  DeletePipelineArg,
  GeneratePipelineSchemaArg,
} from './pipelinesApi.types'

type GenerateKotlinDslArg = {
  pipelineId: PipelineId
  version?: string
  body: KotlinDslRequestBody
}

const url = '/app/pipeline'
const urlAppRestPipelines = 'app/rest/pipelines'
const urlAppRestProjects = 'app/rest/projects'
const normalizeBuildStepDescriptions = (data: GetBuildStepDescription): BuildStepDescriptions => {
  const stepsCandidate = (data as {steps?: BuildStepDescriptions}).steps
  return stepsCandidate && typeof stepsCandidate === 'object'
    ? stepsCandidate
    : (data as BuildStepDescriptions)
}

const processNewPipeline = ({
  vcsRoot,
  settings,
  integrations,
  additionalVcsRoots,
  yaml,
  ...rest
}: NewPipeline): NewPipelineDto => ({
  ...rest,
  ...preformatPipelineVcsData({vcsRoot, additionalVcsRoots}),
  integrations: produce(integrations, getPreformattingIntegrations),
  yaml:
    yaml ??
    stringifyPipeline(settings, {
      replacer: getPreformattingSettings,
      noRefs: true,
    }),
})

function tryToParsePipeline(yaml: string): Partial<PipelineSettings> {
  try {
    if (yaml) {
      const yamlJsonPipeline = parsePipeline(yaml)

      return getPipelinesSettingsWithoutDeprecatedFields(yamlJsonPipeline)
    } else {
      return {}
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    return {}
  }
}

const processPipeline = (pipeline: PipelineDto): Pipeline => {
  const settings = tryToParsePipeline(pipeline.yaml)

  return {...pipeline, settings}
}

export const pipelinesApi = restBaseApi.injectEndpoints({
  endpoints: build => ({
    getRepositoryBranches: build.query<string[], GetRepositoryBranchesArg>({
      query: ({parentProjectId = getAccountRootProjectId(), ...queryArg}) => ({
        url: `${url}/repository/branches?parentProjectExtId=${parentProjectId}`,
        method: 'POST',
        body: queryArg,
      }),
      transformResponse: (data: {values: string[]}) => data.values,
    }),
    repositoryTestConnection: build.mutation<
      {status: 'OK' | 'ERROR'; errors?: {message: string}[]},
      RepositoryTestConnectionArg
    >({
      query: ({parentProjectId = getAccountRootProjectId(), body}) => ({
        url: `${url}/repository/testConnection?parentProjectExtId=${parentProjectId}`,
        method: 'POST',
        body,
      }),
    }),
    checkVersionedSettings: build.query<boolean, CheckVersionedSettingsArg>({
      query: queryArg => ({
        url: `${url}/repository/checkVersionedSettings`,
        method: 'POST',
        body: queryArg,
      }),
    }),
    getAllPipelines: build.query<Pipeline[], {parentProjectId?: ProjectId}>({
      query: ({parentProjectId = getAccountRootProjectId()}) => ({
        url: `${url}?parentProjectExtId=${parentProjectId}`,
      }),
      transformResponse: (data: PipelineDto[]) => data.map(processPipeline),
      providesTags: data => [
        ...(data?.map(({id}) => ({type: 'Pipeline' as const, id})) ?? []),
        {type: 'Pipeline', id: 'LIST'},
      ],
    }),
    getPipelineById: build.query<Pipeline, string>({
      query: id => ({url: `${url}/${id}`}),
      transformResponse: processPipeline,
      providesTags: data => [{type: 'Pipeline', id: `${data?.id}-SINGLE`}],
    }),
    createNewPipeline: build.mutation<Pipeline, CreateNewPipelineArg>({
      query: ({parentProjectId = getAccountRootProjectId(), body}) => ({
        url: `${url}?parentProjectExtId=${parentProjectId}`,
        method: 'POST',
        body: processNewPipeline(body),
      }),
      transformResponse: processPipeline,
      invalidatesTags: (_, error) =>
        error == null ? [{type: 'Pipeline', id: 'LIST'}, 'AllProjects', 'Overview'] : [],
    }),
    updatePipeline: build.mutation<Pipeline, UpdatePipelineArg>({
      query: ({id, body}) => ({
        url: `${url}/${id}`,
        method: 'POST',
        body: processNewPipeline(body),
      }),
      transformResponse: processPipeline,
      invalidatesTags: (_, __, {id}) => [{type: 'Pipeline', id}],
      async onQueryStarted({id}, {dispatch, queryFulfilled}) {
        const {data} = await queryFulfilled
        dispatch(pipelinesApi.util.updateQueryData('getPipelineById', id, () => data))
      },
    }),
    deletePipeline: build.mutation<void, DeletePipelineArg>({
      query: ({id}) => ({url: `${url}/${id}`, method: 'DELETE'}),
      async onQueryStarted({id, cacheUpdateDelay}, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled

          const invalidateCacheTags = () => {
            dispatch(
              pipelinesApi.util.invalidateTags([
                {type: 'Pipeline', id},
                {type: 'Pipeline', id: `${id}-SINGLE`},
                'AllProjects',
                'Overview',
              ]),
            )
          }

          if (cacheUpdateDelay !== undefined) {
            setTimeout(invalidateCacheTags, cacheUpdateDelay)
          } else {
            invalidateCacheTags()
          }
        } catch (error) {
          throw error
        }
      },
    }),
    getRunOptimizations: build.query<RunDurations, {pipelineId: PipelineId; runId: number}>({
      query: ({pipelineId, runId}) => ({
        url: `${url}/${pipelineId}/${runId}/optimizations`,
      }),
    }),
    getPipelineOptimizations: build.query<
      PipelineDurations,
      {pipelineId: PipelineId; branch: string}
    >({
      query: ({pipelineId, branch}) => ({
        url: `${url}/${pipelineId}/optimizations`,
        params: {branch},
      }),
    }),
    getPipelineRunnersJsonFromXMLs: build.query<PipelineRunnersJsonFromXMLs, void>({
      query: () => ({
        url: `${url}/runners/jsonFromXMLs`,
      }),
    }),
    getPipelineFeaturesJsonFromXMLs: build.query<PipelineFeaturesJsonFromXMLs, void>({
      query: () => ({
        url: `${url}/runners/features`,
      }),
    }),
    getPipelineProviderJdk: build.query<PipelineProviderJdk[], void>({
      query: () => ({
        url: `${url}/provider/jdk`,
      }),
      transformResponse: (data: {values: PipelineProviderJdk[]}) => data.values,
    }),
    getParametersResolve: build.query<PipelineParametersResolve[], GetParametersResolveArg>({
      query: ({id, body}) => ({
        url: `${url}/${id}/parameters/resolve`,
        method: 'POST',
        body: {
          ...body,
          pipeline: processNewPipeline(body.pipeline),
        },
      }),
      transformResponse: (data: {matches: PipelineParametersResolve[]}) => data.matches,
    }),
    getJobDescriptions: build.query<GetJobDescriptions, GetJobDescriptionsArg>({
      query: ({id, body}) => ({
        url: `${url}/${id}/job-descriptions`,
        method: 'POST',
        body: processNewPipeline(body),
      }),
    }),
    getBuildStepDescription: build.query<BuildStepDescriptions, GetBuildStepDescriptionArg>({
      query: ({id, body}) => ({
        url: `${url}/${id}/buildStepDescription`,
        method: 'POST',
        body: processNewPipeline(body),
      }),
      transformResponse: normalizeBuildStepDescriptions,
    }),
    agentCompatibilities: build.query<AgentCompatibilities, AgentCompatibilitiesArg>({
      query: queryArg => ({
        url: `${url}/${queryArg.pipelineId}/compatibility/agents/${queryArg.jobId}`,
        method: 'POST',
        body: processNewPipeline(queryArg.body),
      }),
    }),
    jobMapAgentCompatibilities: build.query<JobMapAgentCompatibilities, AgentCompatibilitiesArg>({
      query: queryArg => ({
        url: `${url}/${queryArg.pipelineId}/compatibility/agents`,
        method: 'POST',
        body: processNewPipeline(queryArg.body),
      }),
    }),
    getCompatibilityAgentsQueuedJob: build.query<
      AgentCompatibilities,
      GetCompatibilityAgentsQueuedJobArg
    >({
      query: queryArg => ({
        url: `${url}/${queryArg.pipelineId}/compatibility/agents/queuedJob/${queryArg.buildId}`,
      }),
    }),
    getGroupedAvailableVcsProviders: build.query<GroupedAvailableVcsProviders[], {}>({
      query: () => ({
        url: `${url}/provider/vcs/types`,
      }),
      transformResponse: (data: {values: GroupedAvailableVcsProviders[]}) => data.values,
    }),
    getVcsProviders: build.query<VcsProvider[], {parentProjectId?: ProjectId}>({
      query: ({parentProjectId = getAccountRootProjectId()}) => ({
        url: `${url}/provider/vcs?parentProjectExtId=${parentProjectId}`,
      }),
      transformResponse: (data: {values: VcsProvider[]}) => data.values,
    }),
    getRepositories: build.query<GetRepositoriesResponse, GetRepositoriesArg>({
      query: ({connectionId, parentProjectId = getAccountRootProjectId(), ...queryParams}) => ({
        url: `${url}/provider/vcs/${connectionId}/repositories`,
        params: {
          parentProjectExtId: parentProjectId,
          ...queryParams,
        },
      }),
    }),
    getPipelineRunVcsRoots: build.query<VcsRootDto[], GetPipelineRunVcsRootsArg>({
      query: ({pipelineId, runId}) => ({
        url: `${url}/${pipelineId}/${runId}/vcsRoots`,
      }),
      transformResponse: (data: {values: VcsRootDto[]}) => data.values,
    }),
    runPipelineDebug: build.query<DebugResponse, DebugArg>({
      query: ({pipelineId, body, fields}) => ({
        url: `${urlAppRestPipelines}/${pipelineId}/run`,
        method: 'POST',
        body,
        params: {
          fields,
        },
      }),
    }),
    getPersonalNotifications: build.query<PersonalNotificationsDto, NotificationsArg>({
      query: ({pipelineId}) => ({url: `${url}/${pipelineId}/notifications`}),
      providesTags: _ => [{type: 'Pipeline', id: 'PersonalNotifications'}],
    }),
    setPersonalNotifications: build.mutation<
      void,
      {notifications: PersonalNotificationsDto; pipelineId: PipelineId}
    >({
      query: queryArg => ({
        url: `${url}/${queryArg.pipelineId}/notifications`,
        method: 'POST',
        body: queryArg.notifications,
      }),
      invalidatesTags: (_, __) => [{type: 'Pipeline', id: 'PersonalNotifications'}],
    }),
    getProjectSshKeys: build.query<ProjectSshKeys, GetProjectSshKeysArg>({
      query: ({projectId}) => ({
        url: `${urlAppRestProjects}/${projectId}/sshKeys`,
      }),
    }),
    getPipelineSshKeys: build.query<VcsRootDto[], GetPipelineSshKeysArg>({
      query: ({pipelineId}) => ({
        url: `${urlAppRestPipelines}/${pipelineId}/sshKeys`,
      }),
      transformResponse: (data: {values: VcsRootDto[]}) => data.values,
    }),
    uploadPipelineSshKey: build.mutation<
      undefined,
      {pipelineId: PipelineId; file: File; fileName: string}
    >({
      query: ({pipelineId, file, fileName}) => {
        const formData = new FormData()

        formData.append('file', file)
        formData.append('fileName', fileName)

        return {
          url: `${urlAppRestPipelines}/${pipelineId}/sshKeys`,
          method: 'POST',
          body: formData,
          responseHandler: 'text',
        }
      },
    }),
    uploadProjectSshKey: build.mutation<
      undefined,
      {projectId: string; file: string; fileName: string}
    >({
      query: ({projectId, file, fileName}) => ({
        url: `${urlAppRestProjects}/${projectId}/sshKeys?fileName=${fileName}`,
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
          Accept: 'application/json',
        },
        body: file,
      }),
    }),
    getParentParameters: build.query<ProjectWithAncestorsDto, GetParentParametersArg>({
      query: ({parentProjectId}) => ({
        url: `${urlAppRestProjects}/${parentProjectId}?fields=id,name,parentProjectId,parameters(property(name,value,type)),ancestorProjects(project(id,name,parentProjectId))`,
      }),
      transformResponse: (response: any): ProjectWithAncestorsDto => {
        const transformParameters = (params: any): Record<string, string> | undefined => {
          if (!params?.property) {
            return undefined
          }
          return params.property.reduce(
            (acc: Record<string, string>, prop: {name: string; value: string}) => {
              acc[prop.name] = prop.value
              return acc
            },
            {},
          )
        }

        const transformParametersWithType = (params: any): ParameterWithType[] | undefined => {
          if (!params?.property) {
            return undefined
          }
          return params.property.map((prop: {name: string; value: string; type?: any}) => ({
            name: prop.name,
            value: prop.value,
            type: prop.type,
          }))
        }

        return {
          ...response,
          parameters: transformParameters(response.parameters),
          parametersWithType: transformParametersWithType(response.parameters),
          ancestorProjects: response.ancestorProjects
            ? {
                project: response.ancestorProjects.project,
              }
            : undefined,
        }
      },
    }),
    generateKotlinDsl: build.mutation<string, GenerateKotlinDslArg>({
      query: ({pipelineId, version = 'latest', body}) => ({
        url: `${url}/${pipelineId}/kotlinDsl?version=${version}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/plain',
        },
        body: JSON.stringify(body),
        responseHandler: 'text',
      }),
    }),
    generatePipelineSchema: build.query<PipelineYamlSchema, GeneratePipelineSchemaArg>({
      query: ({pipelineId, body}) => ({
        url: `${url}/schema/generate`,
        method: 'POST',
        body: processNewPipeline(body),
        params: {pipelineId},
      }),
    }),
    getEffectiveVersionedSettingsConfig: build.query<
      EffectiveVersionedSettingsConfig,
      GetEffectiveVersionedSettingsConfigArg
    >({
      query: ({projectId}) => ({
        url: `${urlAppRestProjects}/${projectId}/versionedSettings/config/effective`,
        method: 'GET',
      }),
    }),
  }),
})
